import { useMutation, useQueryClient } from '@tanstack/react-query';
import { buildAttachedResource } from './helper';
import type { UpdateActivityPayload } from './interfaces';
import { falconClient } from '@/data/@clients';
import { clientKeys } from '@/data/@clients/client-keys';
import { baseUrl } from '@/data/idp/base';
import { type Activity, type Plan } from '@/data/idp/plans/interfaces';

export const useUpdatePlanActivity = (activity: Activity) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateActivityPayload) => {
      const attachedResource = buildAttachedResource(payload.attached_resource);

      const response = await falconClient.patch(
        `${baseUrl}/plans/${activity.plan_id}/activities/${activity.id}`,
        {
          activity: {
            status: payload.status ?? activity.status,
            title: payload.title ?? activity.title,
            description: payload.description ?? activity.description,
          },
          attached_resource: attachedResource,
        }
      );

      return response.data as Activity;
    },
    onSuccess: (updatedActivity: Activity) => {
      const planData = queryClient.getQueryData<Plan>([
        clientKeys.usePlanShow,
        activity.plan_id,
      ]);

      if (planData) {
        queryClient.setQueryData<Plan>(
          [clientKeys.usePlanShow, activity.plan_id],
          {
            ...planData,
            activities: planData.activities.map((currentActivity) =>
              currentActivity.id === activity.id
                ? { ...currentActivity, ...updatedActivity }
                : currentActivity
            ),
          }
        );
      }
      queryClient.invalidateQueries({
        queryKey: [clientKeys.usePlanActivitiesIndex, activity.plan_id],
      });
      queryClient.invalidateQueries({
        queryKey: [clientKeys.usePlansIndex],
      });
      queryClient.invalidateQueries({
        queryKey: [clientKeys.usePlanShow, activity.plan_id],
      });
    },
  });
};
